// "Not at the same time" instead of "Full" (availability panel, option-led).
//
// Some options can never start at the same time as each other while each one
// runs fine on its own: Fijnland has one outride guide, and every ride needs
// that guide for the whole booking, so "Behind Spice Route" and "To the Top"
// together have no start on any day — though there are more horses, and each
// ride alone has times. Calling that day "Full" sends the guest away. Instead,
// once the combination comes back empty on a day, the panel asks the same
// bands RPC about each chosen option ALONE (at the whole party's count, and at
// its own count) and offers the way out: everyone on one option ("Ride
// together" for riders, "Go together" for anyone else), or, when no one
// option takes the whole party, one option now and the rest as a separate
// visit ("One at a time").
//
// Everything here is arithmetic over those answers; the words stay generic
// (any service can have options that cannot run side by side), in the
// service's own guest noun and verb (togetherVerb). No runtime imports beyond availability-logic, so
// e2e/together-logic.spec.ts runs it under Node.

import { hasPickableStart, joinAnd, type Band, type DayStatus, type Granularity, type OffsetAt, type SelectionEntry } from "./availability-logic";

/** One chosen option, as the panel holds it. */
export type TogetherOption = {
  id: string;
  name: string;
  /** Its own headcount in the choice. */
  guests: number;
  /** The fewest and the most one booking of it takes (the stepper's floor and ceiling). */
  min: number;
  max: number;
};

/** One single-option read: this option alone, at this headcount. */
export type AloneQuery = { key: string; unitId: string; guests: number; entry: SelectionEntry };

export const aloneKey = (unitId: string, guests: number) => `${unitId}@${guests}`;

/** The party the chosen options add up to. */
export function combinedGuests(options: readonly { guests: number }[]): number {
  return options.reduce((n, o) => n + o.guests, 0);
}

/**
 * The single-option reads worth making for a choice of two or more options:
 * each option at the whole party's count (only when that count is one it can
 * take) and at its own count. Empty for a choice of one option — there is no
 * "together" to fall back from.
 */
export function aloneQueries(selection: readonly SelectionEntry[], options: readonly TogetherOption[]): AloneQuery[] {
  if (selection.length < 2) return [];
  const byId = new Map(options.map((o) => [o.id, o]));
  const total = selection.reduce((n, e) => n + e.guests, 0);
  const out = new Map<string, AloneQuery>();
  for (const e of selection) {
    const o = byId.get(e.service_unit_id);
    if (!o) continue;
    if (total >= o.min && total <= o.max) {
      const key = aloneKey(o.id, total);
      out.set(key, { key, unitId: o.id, guests: total, entry: { ...e, guests: total } });
    }
    const own = aloneKey(o.id, e.guests);
    if (!out.has(own)) out.set(own, { key: own, unitId: o.id, guests: e.guests, entry: { ...e } });
  }
  return [...out.values()];
}

/** On one day: the chosen options that have a start ALONE — for the whole party, and for their own headcount. */
export type DayAlone = { whole: string[]; own: string[] };

export function dayAlone(i: {
  /** Per query key: that read's bands for this day (undefined = not asked). */
  bandsFor: (key: string) => readonly Band[] | undefined;
  options: readonly TogetherOption[];
  gran: Granularity;
  offsetAt: OffsetAt;
  nowMs?: number;
}): DayAlone {
  const total = combinedGuests(i.options);
  const has = (key: string) => {
    const b = i.bandsFor(key);
    return !!b && hasPickableStart(b, i.gran, i.offsetAt, i.nowMs);
  };
  return {
    whole: i.options.filter((o) => total >= o.min && total <= o.max && has(aloneKey(o.id, total))).map((o) => o.id),
    own: i.options.filter((o) => has(aloneKey(o.id, o.guests))).map((o) => o.id),
  };
}

/**
 * A day's status once the single-option reads are known. Only a day the
 * combination left "full" or "not_offered" changes: "checking" while those reads are out,
 * "together" when one option takes the whole party that day, "apart" when
 * only separate visits fit, else it keeps the original reason. A failed read
 * (null) also preserves the combination's own answer.
 */
export function togetherStatus(base: DayStatus, alone: DayAlone | "pending" | null): DayStatus {
  if (base !== "full" && base !== "not_offered") return base;
  if (alone === "pending") return "checking";
  if (!alone) return base;
  if (alone.whole.length > 0) return "together";
  if (alone.own.length > 0) return "apart";
  return base;
}

/**
 * The verb the "together" words use, from the service's guest noun: riders
 * "ride" ("Ride together", "can't ride out at the same time"); anyone else
 * "go", which reads for any service.
 */
export function togetherVerb(noun: { one: string; other: string }): "ride" | "go" {
  const one = noun.one.trim().toLowerCase();
  const other = noun.other.trim().toLowerCase();
  return one === "rider" || other === "riders" ? "ride" : "go";
}

export type TogetherAction = {
  /** "whole": this option alone for the whole party; "keep": this option alone at its own count. */
  kind: "whole" | "keep";
  unitId: string;
  guests: number;
  label: string;
};

/**
 * What the panel says under the time strip on a "together" or "apart" day,
 * and the buttons that act on it. Null when no chosen option has a start on
 * its own that day either: the day is plainly full, and emptyDayCopy says so.
 */
export function togetherNudge(i: {
  dayShort: string;
  noun: { one: string; other: string };
  options: readonly TogetherOption[];
  alone: DayAlone;
}): { status: "together" | "apart"; text: string; actions: TogetherAction[] } | null {
  const n = (g: number) => `${g} ${g === 1 ? i.noun.one : i.noun.other}`;
  const total = combinedGuests(i.options);
  const names = joinAnd(i.options.map((o) => o.name));
  const verb = togetherVerb(i.noun);
  const head = `${names} can't ${verb} out at the same time on ${i.dayShort}`;
  const two = i.options.length === 2;
  const whole = i.options.filter((o) => i.alone.whole.includes(o.id));
  if (whole.length > 0) {
    return {
      status: "together",
      text: `${head}. ${verb === "ride" ? "Ride" : "Go"} together on one of them instead:`,
      actions: whole.map((o) => ({
        kind: "whole",
        unitId: o.id,
        guests: total,
        label: `All ${n(total)} on ${o.name}`,
      })),
    };
  }
  const own = i.options.filter((o) => i.alone.own.includes(o.id));
  if (own.length === 0) return null;
  const most = Math.max(...i.options.map((o) => o.max));
  const why =
    total > most
      ? `${n(total)} is more than ${two ? "either" : "any one"} takes (up to ${most})`
      : `${two ? "neither" : "none"} has room for all ${n(total)} that day`;
  const rest = two ? "book the other as a separate visit" : "book the others as separate visits";
  return {
    status: "apart",
    text: `${head}, and ${why}. Keep one, and ${rest}:`,
    actions: own.map((o) => ({
      kind: "keep",
      unitId: o.id,
      guests: o.guests,
      label: `Keep ${o.name} for ${n(o.guests)}`,
    })),
  };
}
